import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Store,
  ArrowRight,
  ShieldCheck,
  Users,
  Hammer,
} from "lucide-react";
import { Link } from "react-router-dom";

import "./AboutCivilink.css";
import logo from "../../assets/logo.png";

export default function AboutCivilink() {
  const navigate = useNavigate();

  const [showMarketplacePopup, setShowMarketplacePopup] = useState(false);

useEffect(() => {
  const timer = setTimeout(() => {
    setShowMarketplacePopup(true);
  }, 2000);

  return () => clearTimeout(timer);
}, []);

  return (
    <>
      <Helmet>
        <title>
          Welcome to Civilink Private Limited | India's Digital Construction
          Ecosystem
        </title>

        <meta
          name="description"
          content="Welcome to Civilink Private Limited. Discover India's trusted digital construction ecosystem connecting homeowners, engineers, architects, contractors, suppliers and vendors through one platform."
        />

        <link
          rel="canonical"
          href="https://www.civilink.in/about"
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:title"
          content="Welcome to Civilink Private Limited"
        />

        <meta
          property="og:description"
          content="Building India's Trusted Digital Construction Ecosystem."
        />

        <meta
          property="og:url"
          content="https://www.civilink.in/about"
        />

        <meta
          property="og:image"
          content="https://www.civilink.in/logo512.png"
        />

        <meta
          property="og:site_name"
          content="Civilink"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Welcome to Civilink Private Limited"
        />

        <meta
          name="twitter:description"
          content="Building India's Trusted Digital Construction Ecosystem."
        />

        <meta
          name="twitter:image"
          content="https://www.civilink.in/logo512.png"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Civilink Private Limited",
            url: "https://www.civilink.in",
            logo: "https://www.civilink.in/logo512.png",
            email: "civilink.official@gmail.com",
            telephone: "+91-9344425095",
            sameAs: [
              "https://instagram.com/civilink.official",
              "https://www.linkedin.com/in/civilink-private-limited-0905313a7/",
            ],
          })}
        </script>
      </Helmet>

      <div className="about-page">

        {/* ================= HERO ================= */}

        <section className="hero-section">

          <div className="hero-logo">

            <img
              src={logo}
              alt="Civilink Private Limited Logo"
            />

          </div>

          <h1>Civilink Private Limited</h1>

          <p className="hero-tagline">
            Building India's Trusted Digital Construction Ecosystem
          </p>

          <p className="hero-subtitle">
            One Platform. Endless Construction Solutions.
          </p>

        </section>

        {/* ================= WELCOME ================= */}

        <section className="welcome-card">

          <div className="section-title">

            <Building2 size={26} />

            <h2>Welcome to Civilink</h2>

          </div>

          <p>

            Welcome to <strong>Civilink Private Limited</strong>.

          </p>

          <p>

            We are building a trusted digital platform that connects
            homeowners, engineers, architects, contractors,
            suppliers and vendors through one ecosystem.

          </p>

          <p>

            Whether you're planning your dream home,
            searching for construction materials,
            or looking for trusted professionals,
            Civilink helps make construction simpler,
            smarter and more connected.

          </p>

        </section>

        {/* ================= MARKETPLACE ================= */}

        <section className="marketplace-card">

          <div className="section-title">

            <Store size={26} />

            <h2>Explore Construction Marketplace</h2>

          </div>

          <p>

            Browse construction materials from trusted vendors
            across multiple categories.

          </p>

          <p>

            Find quality products for residential,
            commercial and infrastructure projects
            from one trusted marketplace.

          </p>

          <button
            className="primary-btn"
            onClick={() => navigate("/vendor")}
          >

            Explore Marketplace

            <ArrowRight size={18} />

          </button>

        </section>

        {/* ================= ABOUT ================= */}

        <section className="about-card">

          <div className="section-title">

            <ShieldCheck size={26} />

            <h2>About Civilink Private Limited</h2>

          </div>

          <p>

            Civilink Private Limited is an Indian Construction Technology
            (ConTech) company dedicated to transforming the construction
            industry through digital innovation.

          </p>

          <p>

            Our platform brings together homeowners,
            engineers, architects,
            contractors,
            suppliers,
            vendors and construction professionals
            into one trusted digital ecosystem.

          </p>

          <p>

            We believe construction should be
            transparent,
            efficient,
            reliable
            and accessible for everyone.

          </p>

        </section>
                {/* ================= MISSION ================= */}

        <section className="about-card">

          <div className="section-title">

            <Users size={26} />

            <h2>Our Mission</h2>

          </div>

          <p>

            To simplify construction through technology by connecting
            homeowners, professionals, suppliers and vendors on one
            trusted digital platform.

          </p>

          <p>

            We aim to make construction more transparent,
            efficient and accessible for everyone by bringing
            people, products and services together.

          </p>

        </section>

        {/* ================= VISION ================= */}

        <section className="about-card">

          <div className="section-title">

            <Building2 size={26} />

            <h2>Our Vision</h2>

          </div>

          <p>

            To become India's most trusted digital construction
            ecosystem empowering every construction project with
            technology, trust and innovation.

          </p>

        </section>

        {/* ================= WHAT WE OFFER ================= */}

        <section className="about-card">

          <div className="section-title">

            <Hammer size={26} />

            <h2>What We Offer</h2>

          </div>

          <p className="section-description">

            Civilink is designed to support the complete construction
            ecosystem through one easy-to-use digital platform.

          </p>

          <div className="service-grid">

            <div className="service-item">
              <h3>Construction Materials</h3>
              <p>
                Discover quality construction materials from trusted
                vendors across multiple categories.
              </p>
            </div>

            <div className="service-item">
              <h3>Verified Professionals</h3>
              <p>
                Connect with engineers, architects and experienced
                construction professionals.
              </p>
            </div>

            <div className="service-item">
              <h3>Trusted Vendors</h3>
              <p>
                Buy products from reliable suppliers and local
                construction businesses.
              </p>
            </div>

            <div className="service-item">
              <h3>Equipment Rental</h3>
              <p>
                Rent construction machinery and equipment whenever
                your project requires.
              </p>
            </div>

            <div className="service-item">
              <h3>Construction Services</h3>
              <p>
                Find contractors, interior designers and service
                providers for every stage of construction.
              </p>
            </div>

            <div className="service-item">
              <h3>Digital Construction Platform</h3>
              <p>
                One place to discover products, services,
                professionals and business opportunities.
              </p>
            </div>

          </div>

        </section>

        {/* ================= WHY CHOOSE ================= */}

        <section className="about-card">

          <div className="section-title">

            <ShieldCheck size={26} />

            <h2>Why Choose Civilink?</h2>

          </div>

          <div className="feature-grid">

            <div className="feature-item">
              Trusted Construction Platform
            </div>

            <div className="feature-item">
              Verified Professionals
            </div>

            <div className="feature-item">
              Quality Construction Materials
            </div>

            <div className="feature-item">
              Transparent Marketplace
            </div>

            <div className="feature-item">
              Easy-to-Use Platform
            </div>

            <div className="feature-item">
              Technology-Driven Solutions
            </div>

            <div className="feature-item">
              Customer First Approach
            </div>

            <div className="feature-item">
              Growing Construction Community
            </div>

          </div>

        </section>

        {/* ================= OUR COMMITMENT ================= */}

        <section className="about-card">

          <div className="section-title">

            <Building2 size={26} />

            <h2>Our Commitment</h2>

          </div>

          <p>

            At Civilink Private Limited, we are committed to building
            a platform that brings trust, transparency and convenience
            to the construction industry.

          </p>

          <p>

            We continuously work to improve our technology,
            expand our marketplace and create better opportunities
            for customers, professionals and businesses across India.

          </p>

          <p>

            Every new feature we build is focused on making
            construction easier, faster and more connected.

          </p>

        </section>

                {/* ================= COMPANY INFORMATION ================= */}

        <section className="about-card">

          <div className="section-title">

            <Building2 size={26} />

            <h2>Company Information</h2>

          </div>

          <table className="info-table">

            <tbody>

              <tr>
                <td>Company Name</td>
                <td>Civilink Private Limited</td>
              </tr>

              <tr>
                <td>CIN</td>
                <td>U47912TN2026PTC189283</td>
              </tr>

              <tr>
                <td>Founder & CEO</td>
                <td>Madhaw Ashokkumar</td>
              </tr>

              <tr>
                <td>Industry</td>
                <td>Construction Technology (ConTech)</td>
              </tr>

              <tr>
                <td>Country</td>
                <td>India</td>
              </tr>

              <tr>
                <td>Registered Office</td>
                <td>
                  No. 925,
                  <br />
                  Kambanur,
                  <br />
                  Tirupathur Taluk,
                  <br />
                  Karaikudi,
                  <br />
                  Tamil Nadu,
                  <br />
                  India
                </td>
              </tr>

            </tbody>

          </table>

        </section>

        {/* ================= CONTACT ================= */}

        <section className="about-card">

          <div className="section-title">

            <Users size={26} />

            <h2>Connect With Us</h2>

          </div>

          <p className="section-description">

            We'd love to hear from you. Connect with Civilink through any
            of the platforms below.

          </p>

          <div className="contact-grid">

            <a
              className="contact-btn"
              href="https://www.civilink.in"
              target="_blank"
              rel="noreferrer"
            >
              <strong>Official Website</strong>
              <span>www.civilink.in</span>
            </a>

            <a
              className="contact-btn"
              href="mailto:civilink.official@gmail.com"
            >
              <strong>Email</strong>
              <span>civilink.official@gmail.com</span>
            </a>

            <a
              className="contact-btn"
              href="https://wa.me/919344425095"
              target="_blank"
              rel="noreferrer"
            >
              <strong>WhatsApp</strong>
              <span>+91 93444 25095</span>
            </a>

            <a
              className="contact-btn"
              href="https://instagram.com/civilink.official"
              target="_blank"
              rel="noreferrer"
            >
              <strong>Instagram</strong>
              <span>@civilink.official</span>
            </a>

            <a
              className="contact-btn"
              href="https://www.linkedin.com/in/civilink-private-limited-0905313a7/"
              target="_blank"
              rel="noreferrer"
            >
              <strong>LinkedIn</strong>
              <span>Civilink Private Limited</span>
            </a>

          </div>

        </section>

        {/* ================= THANK YOU ================= */}

        <section className="thankyou-card">

          <h2>Thank You for Visiting</h2>

          <p>

            Thank you for taking the time to learn about
            <strong> Civilink Private Limited</strong>.

          </p>

          <p>

            We are committed to creating a smarter, more connected
            and trusted future for India's construction industry.

          </p>

          <button
            className="primary-btn"
            onClick={() => navigate("/vendor")}
          >
            Explore Marketplace
            <ArrowRight size={18} />
          </button>

        </section>

        {/* ================= FOOTER ================= */}

        <footer className="about-footer">

          <img
            src={logo}
            alt="Civilink Private Limited Logo"
            className="footer-logo"
          />

          <h3>Civilink Private Limited</h3>

          <p className="footer-tagline">
            Building India's Trusted Digital Construction Ecosystem
          </p>

          <p className="footer-text">
            Connecting People.
            <br />
            Building Trust.
            <br />
            Creating the Future of Construction.
          </p>

          <div className="footer-divider"></div>

          <p className="footer-version">
            Version 1.0.0
          </p>

          <p className="footer-copy">
            © 2026 Civilink Private Limited.
            <br />
            All Rights Reserved.
          </p>

          <div className="contact-grid">

  <Link
    className="contact-btn"
    to="/privacy-policy"
  >
    <strong>Privacy Policy</strong>
    <span>Learn how we collect, use and protect your information.</span>
  </Link>

  <Link
    className="contact-btn"
    to="/terms"
  >
    <strong>Terms & Conditions</strong>
    <span>Read the rules governing your use of the Civilink Platform.</span>
  </Link>

</div>

        </footer>
        {showMarketplacePopup && (
  <div
    className="marketplace-popup-overlay"
    onClick={() => setShowMarketplacePopup(false)}
  >
    <div
      className="marketplace-popup"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="marketplace-close"
        onClick={() => setShowMarketplacePopup(false)}
      >
        ✕
      </button>

      <img
        src="/images/marketplace-popup.jpg"
        alt="Civilink Marketplace"
      />

      <button
        className="marketplace-explore"
        onClick={() => {
          setShowMarketplacePopup(false);
          
          navigate("/vendor");
        }}
      >
        Explore Marketplace →
      </button>
    </div>
  </div>
)}

      </div>

    </>
  );
}